/**
 * Backup Health & Exclusion Management API client (Spec 211 — T021-UI/T019/T023)
 *
 * Typed frontend client for the Backup Health surface: problematic-target
 * diagnosis (`vw_backup_problems`), the polymorphic exclusion store, and the
 * online remediation actions (exclude / re-include / repoint / retry).
 *
 * Contract: specs/211-backup-health-exclusion-management/contracts/rest-api.md
 * (base `/api/backup/health`; mutations are Admin+Operator and audited).
 *
 * Author: Peter Dobler (Dobler Data Solutions)
 * Feature: 211-backup-health-exclusion-management
 */

import apiClient from '../services/api';

/** The three protected asset classes — the exclusion + diagnosis model is polymorphic across them. */
export type BackupHealthEntityType = 'lakehouse' | 'warehouse' | 'workspace';

/**
 * The classifier vocabulary (research R1). `operator` is the deliberate-governance
 * category an operator picks when excluding an otherwise-fine entity.
 */
export type ReasonCategory =
  // failure categories (classifier output, from a failed run's error text)
  | 'storage_unreachable'
  | 'oom'
  | 'sp_forbidden'
  | 'empty'
  | 'timeout'
  | 'schema_error'
  | 'config_error'
  | 'unknown'
  // entity-state reasons (derived by vw_backup_problems for unprotected entities)
  | 'never_run'
  | 'in_progress'
  | 'no_recovery_point'
  | 'unsupported'
  | 'capture_failed'
  // manual/governance
  | 'operator'
  // display-only: a settled run stamped by the exclusion flow (surfaces after re-include;
  // never an operator input — Spec 214 FR-007/D7)
  | 'excluded';

/** One problematic backup target (no successful backup) — from `vw_backup_problems`. */
export interface BackupProblem {
  entityType: BackupHealthEntityType;
  entityId: string;
  displayName: string;
  reasonCategory: ReasonCategory;
  detail: string;
  lastSeen: string | null;
  /** Spec 236 T044: 'occurred' (a run failed) or 'predicted' (the readiness pass expects one to). A predicted
   *  problem's reasonCategory is its readiness code's category ('recoverability', 'access', …), outside
   *  ReasonCategory, so render it with readinessCategoryLabel rather than the reason labels. */
  phase?: 'occurred' | 'predicted' | null;
}

/** One active exclusion — the single source of exclusion truth (`dbo.backup_exclusions`). */
export interface BackupExclusion {
  id: string;
  entityType: BackupHealthEntityType;
  entityId: string;
  displayName: string;
  reasonCategory: ReasonCategory;
  note: string;
  excludedBy: string | null;
  excludedAt: string;
}

export interface CreateExclusionInput {
  entityType: BackupHealthEntityType;
  entityId: string;
  reasonCategory: ReasonCategory;
  note: string;
}

/**
 * Repoint result. NOTE the backend's `prepared` flag means **"a Prepare-destination step is
 * needed"** (grant missing), NOT "successfully repointed" — locked by the service tests:
 *   • backup-capable  → reassigned  → `prepared:false` + `accountName`   (SUCCESS)
 *   • grant missing    → prepare step → `prepared:true`  + `prepareUrl`   (ACTION NEEDED)
 *   • workspace / not-found → refusal → `prepared:false` + `reason`       (NO-OP)
 */
export interface RepointResult {
  prepared: boolean;
  prepareUrl?: string;
  accountName?: string;
  reason?: string;
}

/** The Spec-208 "Prepare destination" command bundle the operator runs as themselves (app can't self-grant). */
export interface StoragePrepCommand {
  accountName: string;
  env: string;
  armResourceId: string | null;
  spAppId: string;
  fullBlueprintCommand: string;
  minimalRbacCommand: string | null;
  cloudShellUrl: string;
  likelyProvisioned: boolean;
  loginHint: string;
}

/** Spec 214 FR-006: the HONEST launch contract — `launched:false` carries the refusal
 *  reason (already running / no storage / at cap); a launch reports its execution name.
 *  (The old `{ runId }` shape never existed server-side — every toast read "run undefined"
 *  and refusals showed as green successes.) */
export interface RetryResult {
  launched: boolean;
  executionName?: string;
  reason?: string;
}

type Wrapped<T> = { success: boolean; data: T };

const BASE = '/api/v1/backup/health';

export const backupHealthApi = {
  /** Problematic entities across all three asset classes (the Backup Health list). */
  problems: async (): Promise<BackupProblem[]> =>
    (await apiClient.get<Wrapped<BackupProblem[]>>(`${BASE}/problems`)).data,

  /** Active exclusions (with quick who/when for display). */
  exclusions: async (): Promise<BackupExclusion[]> =>
    (await apiClient.get<Wrapped<BackupExclusion[]>>(`${BASE}/exclusions`)).data,

  /** Create an active exclusion (note required); deactivates schedules + settles running runs server-side. */
  exclude: async (input: CreateExclusionInput): Promise<{ id: string }> =>
    (await apiClient.post<Wrapped<{ id: string }>>(`${BASE}/exclusions`, input)).data,

  /** Re-include an excluded entity — it re-enrolls on the next materialize. */
  reinclude: async (id: string): Promise<{ ok: boolean }> =>
    (await apiClient.delete<Wrapped<{ ok: boolean }>>(`${BASE}/exclusions/${id}`)).data,

  /** Repoint an entity's primary backup storage to a reachable account (Spec-208 path). */
  repoint: async (
    entityType: BackupHealthEntityType,
    entityId: string,
    storageAccountId: string
  ): Promise<RepointResult> =>
    (
      await apiClient.post<Wrapped<RepointResult>>(
        `${BASE}/${entityType}/${entityId}/repoint`,
        { storageAccountId }
      )
    ).data,

  /** The Spec-208 grant/prepare command for a destination account (operator runs it in Cloud Shell). */
  prepCommand: async (storageAccountId: string): Promise<StoragePrepCommand> =>
    (await apiClient.get<Wrapped<StoragePrepCommand>>(`/api/v1/backup/storage/${storageAccountId}/prep-command`)).data,

  /** Retry a problematic entity's backup via the existing size-routed trigger. */
  retry: async (
    entityType: BackupHealthEntityType,
    entityId: string,
    backupType?: string
  ): Promise<RetryResult> =>
    (
      await apiClient.post<Wrapped<RetryResult>>(
        `${BASE}/${entityType}/${entityId}/retry`,
        backupType ? { backupType } : {}
      )
    ).data,
};

export default backupHealthApi;
